import { Box } from "@chakra-ui/react";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Callout, Code, Flex, Progress, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { buildLoginRedirectPath } from "../../authentication/core/client/login-redirect";
import { routes } from "../../routes";
import {
  captureCampingCheckout,
  getApiUserErrorMessage,
  isForbiddenApiError,
  isUnauthorizedApiError,
} from "../../shared/api/nest-api";
import { ContactUsButton } from "../../shared/components/contact-us-button";
import { prefetchBookingQuery } from "../../shared/queries/use-booking-query";
import { createMutex } from "../../shared/utils/mutex";
import { useCampingStation } from "../hooks/use-camping-station";
import { useCampingStationSlug } from "../hooks/use-camping-station-slug";
import { useResetCampingState } from "../hooks/use-reset-camping-state";

const mutex = createMutex();

export const CampingRentCapturePage: React.FC = () => {
  const slug = useCampingStationSlug();
  const station = useCampingStation();
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get("checkout_id");

  if (!checkoutId) {
    throw new Error("checkout_id query parameter required");
  }

  const navigate = useNavigate();
  const resetCampingState = useResetCampingState();

  const [error, setError] = useState<unknown>();

  useEffect(() => {
    // avoid capturing the same checkout multiple times,
    // e.g. when component is rendered multiple times in react strict mode
    void mutex.runOnce(async () => {
      try {
        const result = await captureCampingCheckout(station.id, checkoutId);

        await prefetchBookingQuery(result.bookingSlug);

        resetCampingState();

        void navigate(
          routes.camping.booking.success(slug, result.bookingSlug),
          {
            replace: true,
          },
        );
      } catch (error) {
        if (isUnauthorizedApiError(error)) {
          void navigate(
            buildLoginRedirectPath(
              window.location.pathname + window.location.search,
              slug,
            ),
            { replace: true },
          );
          return;
        }

        if (isForbiddenApiError(error)) {
          setError(
            getApiUserErrorMessage(error) ??
              "Diese Station ist mit Ihrer E-Mail-Adresse nicht verfügbar.",
          );
          return;
        }

        // display custom error message in this critical part of the app
        setError(error);
      }
    });
  }, [station.id, checkoutId, slug, navigate, resetCampingState]);

  if (error) {
    const errorMessage =
      typeof error === "string"
        ? error
        : "Es ist leider ein Fehler bei der Buchung aufgetreten!";

    return (
      <Flex direction="column" gap="4">
        <Callout.Root color="red">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>{errorMessage}</Callout.Text>
        </Callout.Root>

        {typeof error !== "string" && (
          <>
            <div>
              Bitte kontaktieren Sie uns damit wir Ihnen schnell und
              unkompliziert helfen können.
            </div>

            <div>
              Nennen Sie uns dabei bitte Ihre Buchungsnummer:{" "}
              <Code>{checkoutId.substring(0, 15)}</Code>.
            </div>

            <ContactUsButton />
          </>
        )}
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" gap="4">
      <Text>
        Bitte haben Sie einen kurzen Moment Geduld während wir Ihre Zahlung
        verarbeiten.
      </Text>

      <Box width="100%">
        <Progress duration="10s" size="3" />
      </Box>
    </Flex>
  );
};
